/**
 * File URL helpers — PocketBase native.
 *
 * PB stores files inline with records under `pb_data/storage/<collId>/<recId>/`
 * and exposes them at `<PB_URL>/api/files/<collId>/<recId>/<filename>`.
 *
 * Use `fileUrl(record, filename)` to build a public URL, or
 * `fileUrl(record, filename, { thumb: '100x100' })` for a server-side
 * generated thumbnail (PB has built-in image resize via the thumb param).
 *
 * --- Caching ---
 * Identical `(recordId, filename, options)` returns the same string from a
 * module-level memo so React props stay referentially stable and the
 * browser's disk cache hits on every repeat render. Same URL on every
 * render also means PB generates the thumb once, then serves from cache.
 */

import { pb } from "./pb";

/* Cache-version token baked into every image URL. Bump this to bust
 * browser/CDN caches app-wide. v2: heals the v2.1-cutover window where
 * imgproxy/FileServe 404s got cached with `immutable, max-age=604800`
 * (ALLOWED_HOSTS + doubled-path bugs) — those poisoned entries never
 * revalidate, so only a *different URL* recovers them. Changing this
 * constant changes every /img + /api/files URL the app builds. */
const IMG_CACHE_VER = "2";

/* ═══════════════════════════════════════════════════════════
   Session cookie — gate for /api/files/* + /img/*
   ═══════════════════════════════════════════════════════════
   Caddy refuses asset requests that don't carry a `pb_auth` cookie.
   We set the cookie after login and clear it on logout. Because
   `<img>` requests include same-origin cookies automatically, every
   image on the page is transparently authed. Signed-out users
   pasting a shared URL get a Caddy 403 — the URL alone is never a
   credential, and the cookie can't be set cross-origin.

   The cookie value is the PB auth token; we don't verify it at the
   Caddy layer (its mere presence is the gate). PB is internal-only
   so nothing external can reach it directly.
   ═══════════════════════════════════════════════════════════ */

/**
 * Session cookie handoff.
 *
 * We originally set `pb_auth` from JavaScript via `document.cookie`. That
 * works on desktop, but mobile Safari (especially ITP + standalone PWA
 * jars) is notoriously flaky about JS-set cookies — they silently don't
 * persist, which meant every image request hit Caddy's cookie gate and
 * returned 403, which is the "no images on phone" bug.
 *
 * Now the cookie is set **server-side** via `POST /api/cl/auth/session`.
 * That emits a proper `Set-Cookie` header (HttpOnly, Secure, SameSite=Lax)
 * that every browser — including every mobile quirk — treats as a
 * first-class cookie. Bonus: HttpOnly means JS can't leak the token.
 *
 * Local dev over plain HTTP still falls back to `document.cookie` because
 * the Secure flag would block the server cookie there.
 */

const COOKIE_NAME = "pb_auth";
let serverCookieInFlight: Promise<void> | null = null;

function writeAuthCookieInsecure(token: string) {
  // Dev-only fallback for http://localhost sessions — the prod flow goes
  // through the server endpoint below.
  if (typeof document === "undefined") return;
  document.cookie = `${COOKIE_NAME}=${encodeURIComponent(token)}; Path=/; SameSite=Lax; Max-Age=604800`;
}

function clearCookieInsecure() {
  if (typeof document === "undefined") return;
  document.cookie = `${COOKIE_NAME}=; Path=/; Max-Age=0; SameSite=Lax`;
}

async function writeAuthCookieFromServer(): Promise<void> {
  if (serverCookieInFlight) return serverCookieInFlight;
  serverCookieInFlight = (async () => {
    try {
      // pb.send attaches the Authorization header automatically from
      // pb.authStore.token. The server reads it and mints a Set-Cookie.
      await pb.send("/api/cl/auth/session", { method: "POST" });
    } catch {
      // If the server endpoint is unreachable (e.g. first-run before the
      // backend image includes auth_cookie.pb.js), fall back to JS cookie
      // so desktop still works. Mobile users may see temporary 403s until
      // the next reload.
      writeAuthCookieInsecure(pb.authStore.token);
    } finally {
      serverCookieInFlight = null;
    }
  })();
  return serverCookieInFlight;
}

async function clearAuthCookieFromServer(): Promise<void> {
  try { await pb.send("/api/cl/auth/logout", { method: "POST" }); }
  catch { clearCookieInsecure(); }
}

/** Keep the session cookie in sync with PB's auth store. Called from
 *  `useAuth` on login/logout. Returns a promise that resolves once the
 *  cookie is fully established (important on login — the caller should
 *  `await` this before navigating, otherwise `<img>` subresources on
 *  the destination page race the cookie and hit 403 at Caddy's gate). */
export async function syncAuthCookie(): Promise<void> {
  if (typeof location !== "undefined" && location.protocol !== "https:") {
    // HTTP dev — synchronous JS cookie (no Secure flag restriction).
    if (pb.authStore.isValid && pb.authStore.token) {
      writeAuthCookieInsecure(pb.authStore.token);
    } else {
      clearCookieInsecure();
      urlCache.clear();
    }
    return;
  }
  // Production HTTPS — go through the server endpoint so the cookie
  // is HttpOnly + Secure + first-class (survives mobile Safari ITP).
  if (pb.authStore.isValid && pb.authStore.token) {
    await writeAuthCookieFromServer();
  } else {
    await clearAuthCookieFromServer();
    urlCache.clear();
  }
}

/* ═══════════════════════════════════════════════════════════ */

export interface FileUrlOptions {
  thumb?: string;
  /** Include the short-lived file token. Defaults to true now that files
   *  are protected. Set explicitly to false only for public assets. */
  withToken?: boolean;
  download?: boolean;
}

/* ═══════════════════════════════════════════════════════════
   optimizedUrl — imgproxy-backed WebP/AVIF transform
   ═══════════════════════════════════════════════════════════
   Every `<img>` in the UI goes through this instead of `fileUrl`.
   It builds a `/img/insecure/<options>/<base64-source>` path that
   Caddy forwards to the `imgproxy` container, which fetches the
   original from the backend (via its IMGPROXY_BASE_URL), re-encodes
   to WebP, and returns it with 7-day immutable cache.

   Originals remain untouched. Downloads still use `fileUrl` for
   the raw asset.
   ═══════════════════════════════════════════════════════════ */

export interface OptimizedUrlOptions {
  /** Target width in px. Height auto (aspect preserved). */
  w: number;
  /** JPEG/WebP quality 1-100. Default 85. */
  q?: number;
  /** Output format. Default `webp`. Use `auto` to let imgproxy pick
   *  based on the browser's Accept header. */
  fmt?: "webp" | "avif" | "auto";
  /** Resize mode. `fit` (default) preserves aspect; `fill` crops to fit. */
  mode?: "fit" | "fill";
}

/** Base64url-safe encode (no padding) — imgroxy plain-URL expects this. */
function b64url(input: string): string {
  // btoa handles ASCII only; encodeURIComponent covers unicode safely.
  const bin = typeof btoa === "function"
    ? btoa(unescape(encodeURIComponent(input)))
    : Buffer.from(input, "utf-8").toString("base64");
  return bin.replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

/**
 * Build an imgproxy URL for a PocketBase file.
 *
 *   optimizedUrl(asset, asset.file, { w: 800 })
 *   optimizedUrl(workspace, workspace.logo, { w: 128, q: 90 })
 *
 * The resulting URL is relative, safe for `<img src>` and SSR-agnostic.
 * Falls back to the raw PB thumb URL if we can't determine a source.
 */
export function optimizedUrl(
  record: RecordLike | null | undefined,
  filename: string | undefined,
  opts: OptimizedUrlOptions,
): string {
  if (!record || !filename) return "";

  // Pre-uploaded absolute URLs bypass the proxy — we don't re-fetch
  // 3rd-party hosted images (legacy Firebase links, etc.).
  if (filename.startsWith("http://") || filename.startsWith("https://")) {
    return filename;
  }

  // Dot-index path (e.g. "references.2") — resolve to actual filename.
  if (filename.includes(".") && filename.split(".")[0] in record) {
    const [field, idx] = filename.split(".");
    const arr = record[field];
    if (Array.isArray(arr)) {
      const f = arr[parseInt(idx, 10)];
      if (f) filename = f;
    }
  }

  const collId = record.collectionId ?? record.collectionName ?? "";
  const recId  = record.id ?? "";
  if (!collId || !recId) return "";

  // Source URL imgproxy will fetch — relative to its IMGPROXY_BASE_URL
  // (http://backend:8090). We pass the PATH only, no host. Files are
  // unprotected at the PB layer so imgproxy's back-channel fetch
  // succeeds; public access is gated at the Caddy edge via the
  // `pb_auth` cookie on the outer `/img/*` route.
  const src = `api/files/${collId}/${recId}/${filename}`;
  const srcEncoded = b64url(src);

  const width  = Math.max(16, Math.floor(opts.w));
  const quality = Math.max(1, Math.min(100, opts.q ?? 85));
  const fmt = opts.fmt ?? "webp";
  const mode = opts.mode ?? "fit";

  // Processing-options segments — see https://docs.imgproxy.net
  const processing = [
    `rs:${mode}:${width}:0`,   // resize: mode, width, height(0 = auto)
    `q:${quality}`,            // quality
    fmt === "auto" ? null : `f:${fmt}`,
    `cb:${IMG_CACHE_VER}`,     // imgproxy cachebuster — no effect on output
  ].filter(Boolean).join("/");

  // `insecure` prefix: URL signing not yet enabled on imgproxy side.
  return `/img/insecure/${processing}/${srcEncoded}`;
}

/** Convenience: DPR-aware srcSet for responsive `<img>` rendering.
 *  Yields `url 1x, url2 2x` pairs. */
export function optimizedSrcSet(
  record: RecordLike | null | undefined,
  filename: string | undefined,
  widthCss: number,
  opts: Omit<OptimizedUrlOptions, "w"> = {},
): string {
  if (!record || !filename) return "";
  const one = optimizedUrl(record, filename, { ...opts, w: widthCss });
  const two = optimizedUrl(record, filename, { ...opts, w: widthCss * 2 });
  return `${one} 1x, ${two} 2x`;
}

type RecordLike = Record<string, any>;

/**
 * Canonical thumb sizes — import from here rather than repeating strings.
 * Using the same size for the same asset across the whole app means the
 * browser caches one URL instead of fetching logo@48, logo@64, logo@120
 * as three separate downloads.
 */
export const THUMB_SIZES = {
  /** Brand logo in TopNav, dropdowns, cards — 2× DPR looks sharp up to ~128 px. */
  LOGO: "256x256",
  /** User avatar in TopNav, comments, etc. */
  AVATAR: "128x128",
  /** Asset card in Library grid / Templates / Dashboard. */
  ASSET_CARD: "800x800",
  /** Asset preview in lightbox before the original loads. */
  ASSET_LIGHTBOX: "1600x1600",
  /** Brand element tile in BrandKit. */
  ELEMENT: "400x400",
} as const;

const urlCache = new Map<string, string>();

function cacheKey(record: RecordLike, filename: string, opts: FileUrlOptions): string {
  return [
    record.id ?? record.collectionId ?? "?",
    filename,
    opts.thumb ?? "",
    opts.download ? "dl" : "",
    opts.withToken ? pb.authStore.token?.slice(0, 8) ?? "" : "",
  ].join("|");
}

export function fileUrl(
  record: RecordLike | null | undefined,
  filename: string | undefined,
  opts: FileUrlOptions = {},
): string {
  if (!record || !filename) return "";

  if (filename.startsWith("http://") || filename.startsWith("https://")) {
    return filename;
  }

  if (filename.includes(".")) {
    const [field, idx] = filename.split(".");
    const arr = record[field];
    if (Array.isArray(arr)) {
      const f = arr[parseInt(idx, 10)];
      if (f) filename = f;
    }
  }

  const key = cacheKey(record, filename!, opts);
  const cached = urlCache.get(key);
  if (cached) return cached;

  const queryParams: Record<string, string> = { cv: IMG_CACHE_VER };
  if (opts.thumb)     queryParams.thumb    = opts.thumb;
  if (opts.download)  queryParams.download = "1";
  // `withToken` is retained for back-compat but now a no-op — session
  // auth happens at the Caddy edge via the `pb_auth` cookie, not a
  // token in the URL. Callers that still pass `withToken: true` get
  // clean URLs that rely on the cookie.

  const url = pb.files.getURL(record as any, filename!, queryParams);
  urlCache.set(key, url);
  return url;
}

/** Drop cached URLs for a record — call after re-uploading a file so new
 *  thumbs are fetched instead of the stale ones. */
export function invalidateFileCache(recordId: string) {
  for (const k of urlCache.keys()) {
    if (k.startsWith(recordId + "|")) urlCache.delete(k);
  }
}

/** Download a private file as a blob. Same-origin `<img>` + `fetch`
 *  automatically carry the `pb_auth` cookie that Caddy's /api/files
 *  gate accepts. */
export async function fetchPrivateMedia(url: string): Promise<string> {
  if (!url) throw new Error("fetchPrivateMedia: empty url");
  const res = await fetch(url, { credentials: "same-origin" });
  if (!res.ok) throw new Error(`Private media fetch failed (${res.status})`);
  const blob = await res.blob();
  return URL.createObjectURL(blob);
}

export async function openPrivateMedia(url: string): Promise<void> {
  const placeholder = window.open("", "_blank");
  try {
    const blobUrl = await fetchPrivateMedia(url);
    if (placeholder) {
      placeholder.location.href = blobUrl;
      setTimeout(() => URL.revokeObjectURL(blobUrl), 60_000);
    } else {
      window.location.href = blobUrl;
    }
  } catch (err) {
    if (placeholder) placeholder.close();
    throw err;
  }
}

export async function downloadPrivateMedia(url: string, filename?: string): Promise<void> {
  const blobUrl = await fetchPrivateMedia(url);
  const a = document.createElement("a");
  a.href = blobUrl;
  a.download = filename || url.split("/").pop()?.split("?")[0] || "download";
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(blobUrl), 60_000);
}

export default fileUrl;
